import { useRef, useState } from 'react'
import { Button, Dialog, Icon, TextField } from '@bloo-team/ui'
import { Trash2, Upload } from '@bloo-team/ui/icons'
import { eventStoreNameFrom, isValidEventStoreName } from '@bloo-team/api-client'
import type { EventStore } from '@bloo-team/api-client'
import {
  canWriteStorage, useDeleteEventStore, usePredelete, useUploadEventStore, type PredeleteImpact,
} from '../lib/storage'

/**
 * Storage · Event stores — the legacy event-store list, rebuilt dense over
 * the captured row (docs/storage-requirements.md §Event stores tab). A store
 * is a saved dataset: a DQL result or an uploaded csv/xlsx that workbooks
 * and enrichment buckets look up.
 *
 * Delete ALWAYS goes through `predelete` first: the impact dialog lists the
 * workbooks and enrichment buckets the backend says reference the store —
 * never a client-side guess — and Confirm echoes that answer back as
 * `predelete_data` (A1). The backend decides whether a referenced store may
 * go, so Confirm stays enabled either way. Upload (A2) asks for the store
 * name — letters, digits, underscore — defaulting to the file's basename.
 * Both writes are engineer-only (`canWriteStorage`).
 */

export interface EventStoresTabProps {
  clusterId: string
  scopeId: string
  stores: EventStore[]
  isPending: boolean
  isError: boolean
  error: unknown
}

const fmtDate = (ms: number | null) =>
  ms === null ? '—' : new Date(ms).toLocaleDateString(undefined, { dateStyle: 'medium' })

/** `DQL`, or `upload · csv` — the chip text. */
export const sourceLabel = (s: Pick<EventStore, 'sourceKind' | 'extension'>): string =>
  s.sourceKind === 'upload' ? `upload${s.extension ? ` · ${s.extension}` : ''}` : 'DQL'

const NAME_HINT = 'Letters, digits and underscore.'

export function EventStoresTab({ clusterId, scopeId, stores, isPending, isError, error }: EventStoresTabProps) {
  const canWrite = canWriteStorage(clusterId, scopeId)
  const scope = { clusterId, scopeId }
  const predelete = usePredelete(scope)
  const remove = useDeleteEventStore(scope)
  const upload = useUploadEventStore(scope)
  const fileRef = useRef<HTMLInputElement>(null)

  /** The row whose impact is being checked or shown. */
  const [target, setTarget] = useState<EventStore | null>(null)
  const [impact, setImpact] = useState<PredeleteImpact | null>(null)
  /** The chosen file awaiting a name. */
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [uploadName, setUploadName] = useState('')

  const askDelete = async (row: EventStore) => {
    setTarget(row)
    setImpact(null)
    try {
      setImpact(await predelete.mutateAsync({ name: row.name }))
    } catch {
      setTarget(null)
    }
  }
  const closeDelete = () => {
    if (remove.isPending) return
    setTarget(null)
    setImpact(null)
  }
  const confirmDelete = async () => {
    if (!target || !impact) return
    await remove.mutateAsync({ name: target.name, predeleteData: impact.raw })
    setTarget(null)
    setImpact(null)
  }

  const onFile = (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    setPendingFile(file)
    setUploadName(eventStoreNameFrom(file.name))
  }
  const closeUpload = () => {
    if (upload.isPending) return
    setPendingFile(null)
    setUploadName('')
  }
  const nameTaken = stores.some((s) => s.name === uploadName)
  const nameError =
    uploadName === '' ? 'A store name is required.'
    : !isValidEventStoreName(uploadName) ? `Invalid store name. ${NAME_HINT}`
    : nameTaken ? 'A store with this name exists.'
    : undefined
  const confirmUpload = async () => {
    if (!pendingFile || nameError) return
    await upload.mutateAsync({ name: uploadName, file: pendingFile })
    setPendingFile(null)
    setUploadName('')
  }

  const busy = predelete.isPending || remove.isPending || upload.isPending
  const failure = predelete.error ?? remove.error ?? upload.error

  return (
    <div className="hud-stack" role="tabpanel" aria-label="Event stores">
      <div className="hud-row" style={{ gap: 'var(--bloo-space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="hud-unit">{stores.length} event store{stores.length === 1 ? '' : 's'}</span>
        {canWrite ? (
          <>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept=".csv,.xlsx"
              aria-label="Store file"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              size="sm"
              className="st-upload"
              style={{ marginLeft: 'auto' }}
              isDisabled={busy}
              onPress={() => fileRef.current?.click()}
            >
              <Icon icon={Upload} size={13} /> {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        ) : null}
      </div>

      {isError ? (
        <div className="bui-alert bui-alert-error" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>
            <span className="bui-alert-title">Could not load event stores</span>
            {error instanceof Error ? error.message : 'Unknown error.'}
          </span>
        </div>
      ) : null}
      {failure ? (
        <div className="bui-alert bui-alert-error" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>
            <span className="bui-alert-title">Event store action failed</span>
            {failure instanceof Error ? failure.message : 'Unknown error.'}
          </span>
        </div>
      ) : null}
      {isPending ? <p className="hud-unit" role="status">Loading event stores…</p> : null}
      {!isPending && !isError && stores.length === 0 ? (
        <p className="hud-unit" role="status">No event stores in this scope.</p>
      ) : null}

      {stores.length > 0 ? (
        <div className="dense-table-wrap">
          <table className="dense-table st-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Source</th>
                <th>Author</th>
                <th>Created</th>
                <th>Modified</th>
                {canWrite ? <th aria-label="Row actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {stores.map((s) => (
                <tr key={s.name} data-source={s.sourceKind}>
                  <td><span className="cell-name">{s.name}</span></td>
                  <td>
                    <span
                      className="bui-chip st-source"
                      data-source={s.sourceKind}
                      title={[s.dataSource, s.fileName ? `file ${s.fileName}` : null].filter(Boolean).join(' · ') || undefined}
                    >
                      {sourceLabel(s)}
                    </span>
                  </td>
                  <td className="mono">{s.author ?? s.createdBy ?? '—'}</td>
                  <td className="mono muted">{fmtDate(s.createdAt)}</td>
                  <td className="mono muted">
                    {fmtDate(s.modifiedAt)}
                    {s.modifiedBy && s.modifiedBy !== (s.author ?? s.createdBy) ? ` · ${s.modifiedBy}` : ''}
                  </td>
                  {canWrite ? (
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        tone="warn"
                        aria-label={`Delete ${s.name}`}
                        isDisabled={busy}
                        onPress={() => void askDelete(s)}
                      >
                        <Icon icon={Trash2} size={13} /> Delete
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Dialog
        open={target !== null}
        onClose={closeDelete}
        title="Delete event store"
        className="sp-close-dialog"
        isDismissable={!remove.isPending}
      >
        {target ? (
          <div className="hud-stack sp-form">
            <p className="hud-unit">
              Delete <span className="mono">{target.name}</span>? Workbooks and enrichment buckets that use this
              store are listed below; the backend decides whether the delete proceeds. This cannot be undone.
            </p>
            {impact === null ? (
              <p className="hud-unit" role="status">Checking what references this store…</p>
            ) : impact.items.length === 0 ? (
              <p className="hud-unit" role="status">Nothing references this store.</p>
            ) : (
              <ul className="st-impact" aria-label="Delete impact">
                {impact.items.map((item, i) => (
                  <li key={`${item.kind}-${item.name}-${i}`} className="hud-row hud-row-between">
                    <span className="mono">{item.name}</span>
                    <span className="muted">{item.kind}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="hud-row" style={{ justifyContent: 'flex-end', gap: 'var(--bloo-space-2)' }}>
              <Button variant="ghost" size="sm" isDisabled={remove.isPending} onPress={closeDelete}>
                Cancel
              </Button>
              <Button
                variant="solid"
                size="sm"
                tone="warn"
                isDisabled={impact === null || remove.isPending}
                onPress={() => void confirmDelete()}
              >
                {remove.isPending ? 'Deleting…' : 'Confirm delete'}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={pendingFile !== null}
        onClose={closeUpload}
        title="Upload event store"
        className="sp-close-dialog"
        isDismissable={!upload.isPending}
      >
        {pendingFile ? (
          <div className="hud-stack sp-form">
            <p className="hud-unit">
              <span className="mono">{pendingFile.name}</span> becomes a store under the name below.
            </p>
            <TextField
              label="Store name"
              value={uploadName}
              onChange={setUploadName}
              hint={nameError ? undefined : NAME_HINT}
              error={nameError}
              isDisabled={upload.isPending}
            />
            <div className="hud-row" style={{ justifyContent: 'flex-end', gap: 'var(--bloo-space-2)' }}>
              <Button variant="ghost" size="sm" isDisabled={upload.isPending} onPress={closeUpload}>
                Cancel
              </Button>
              <Button
                variant="solid"
                size="sm"
                isDisabled={nameError !== undefined || upload.isPending}
                onPress={() => void confirmUpload()}
              >
                {upload.isPending ? 'Uploading…' : 'Upload store'}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  )
}
